// ==================== CONFIGURAZIONI MACCHINE SPELAFILI ====================
//
// Registry estensibile: per aggiungere una nuova macchina, basta aggiungere
// un nuovo entry all'oggetto MACHINES con le proprie sezioni e parametri.
// Il componente UI li renderizzerà automaticamente.

import { TERMINAL_DATA } from './terminalConstants';
import { SEZIONI, SEZIONI_AWG } from './cableConstants';

/**
 * Risolve i dati di un terminale dato il suo codice.
 * Gestisce sia i terminali standard (da TERMINAL_DATA) che le spelature
 * personalizzate in formato `SP<len>/<peel>` (es. "SP5/3").
 * @param {string} code - codice terminale (es. 'PDW', 'SP5/3')
 * @param {Object} terminalData - TERMINAL_DATA o override
 * @returns {{ len: number, peel: number } | null}
 */
const resolveTerminal = (code, terminalData) => {
    if (!code) return null;
    // Spelatura personalizzata: SP<len>/<peel>
    const spMatch = code.match(/^SP(\d+(?:\.\d+)?)\/?(\d+(?:\.\d+)?)$/);
    if (spMatch) {
        return { len: parseFloat(spMatch[1]), peel: parseFloat(spMatch[2]) };
    }
    return terminalData[code] || null;
};

/**
 * Calcola se l'INNER va abilitato (almeno un terminale ha peel > 0)
 */
export const needsInnerStripping = (cable, terminalData = TERMINAL_DATA) => {
    if (!cable.poli || !Array.isArray(cable.poli)) return false;
    return cable.poli.some(polo => {
        const termA = resolveTerminal(polo.terminaleA, terminalData);
        const termB = resolveTerminal(polo.terminaleB, terminalData);
        return (termA && termA.peel > 0) || (termB && termB.peel > 0);
    });
};

/**
 * Registry delle macchine spelafili.
 * Ogni macchina definisce sezioni con parametri calcolabili o manuali.
 */
export const MACHINES = {
    'EW-05': {
        id: 'EW-05',
        name: 'EW-05',
        description: 'Automatic wire cutting stripping and twisting machine',
        /**
         * Genera le sezioni e parametri per un dato cavo multipolare.
         * @param {Object} cable - Il cavo multipolare con .poli[], .lunghezza
         * @param {Object} terminalData - TERMINAL_DATA o override personalizzato
         * @returns {Array} sezioni con parametri calcolati
         */
        computeSections: (cable, terminalData = TERMINAL_DATA) => {
            // Costanti specifiche per la macchina EW-05
            const TWO_PASS_THRESHOLD = 150;
            const SLITTING_THRESHOLD = 300; // Oltre 300mm serve Slitting
            const DEFAULT_SEGMENT_INTERVAL = 100; // Intervallo default per incisioni multi-segment
            const INNER_RETRACT_FACTOR = 0.3;
            const OUTER_RETRACT_FACTOR = 0.2;
            const OUTER_RETRACT_PERCENT = (OUTER_RETRACT_FACTOR * 100).toFixed(0);
            const INNER_RETRACT_PERCENT = (INNER_RETRACT_FACTOR * 100).toFixed(0);

            const poli = cable.poli || [];
            if (poli.length === 0) return [];

            // Calcoli lato A (Left sulla macchina)
            // Strip L = lunghezzaA dei fili (quanto sporgono dalla guaina)
            const lunghezzeA = poli.map(p => p.lunghezzaA || 0);
            const targetStripL = Math.max(...lunghezzeA);

            // Calcoli lato B (Right sulla macchina)
            const lunghezzeB = poli.map(p => p.lunghezzaB || 0);
            const targetStripR = Math.max(...lunghezzeB);

            // Logica 2 passate: se strip > 150mm
            const needsSplitL = targetStripL > TWO_PASS_THRESHOLD;
            const needsSplitR = targetStripR > TWO_PASS_THRESHOLD;
            const hasTwoPass = needsSplitL || needsSplitR;

            // Logica Slitting: se strip > 300mm (2 passate non bastano)
            const needsSlittingL = targetStripL > SLITTING_THRESHOLD;
            const needsSlittingR = targetStripR > SLITTING_THRESHOLD;
            const hasSlitting = needsSlittingL || needsSlittingR;

            // INNER: controlla se serve spelatura interna
            const innerEnabled = needsInnerStripping(cable, terminalData);

            // Calcola parametri INNER (Strip = len, Pull-off = peel)
            // Solo i poli con la lunghezza massima su ogni lato vengono considerati:
            // i poli più corti vengono accorciati manualmente dopo la macchina,
            // quindi non è necessario spelarli.
            let innerStripL = 0, innerPullCombineL = 0;
            let innerStripR = 0, innerPullCombineR = 0;

            if (innerEnabled) {
                const maxLunghezzaA = Math.max(...poli.map(p => p.lunghezzaA || 0));
                const maxLunghezzaB = Math.max(...poli.map(p => p.lunghezzaB || 0));

                poli.forEach(polo => {
                    const termA = resolveTerminal(polo.terminaleA, terminalData);
                    const termB = resolveTerminal(polo.terminaleB, terminalData);
                    // Lato A = Left: solo i poli con la lunghezza A massima
                    if ((polo.lunghezzaA || 0) === maxLunghezzaA && termA && termA.peel > 0) {
                        innerStripL = Math.max(innerStripL, termA.len || 0);
                        innerPullCombineL = Math.max(innerPullCombineL, termA.peel || 0);
                    }
                    // Lato B = Right: solo i poli con la lunghezza B massima
                    if ((polo.lunghezzaB || 0) === maxLunghezzaB && termB && termB.peel > 0) {
                        innerStripR = Math.max(innerStripR, termB.len || 0);
                        innerPullCombineR = Math.max(innerPullCombineR, termB.peel || 0);
                    }
                });
            }

            // Diametro rappresentativo dei fili interni per blade value INNER
            // Usa la mappatura SEZIONI/SEZIONI_AWG per ricavare il diametro dalla sezione
            const sezioni = [...new Set(poli.map(p => p.sezione))];
            const maxSezione = Math.max(...sezioni);
            const sezioneMap = cable.useAWG ? SEZIONI_AWG : SEZIONI;
            const sezioneEntry = sezioneMap.find(s => s.val === maxSezione)
                || sezioneMap.reduce((prev, curr) =>
                    Math.abs(curr.val - maxSezione) < Math.abs(prev.val - maxSezione) ? curr : prev
                );
            const maxDiametro = (sezioneEntry && sezioneEntry.dia !== '') ? sezioneEntry.dia : null;

            const sections = [];

            // === GENERAL: Full Length ===
            sections.push({
                id: 'general',
                label: 'Generale',
                labelKey: 'machineConfig.EW05.sections.general',
                parameters: [
                    {
                        key: 'fullLength',
                        label: 'Full Length',
                        value: cable.lunghezza,
                        unit: 'mm',
                        tooltipKey: 'machineConfig.EW05.general.lenght'
                    },
                    {
                        key: 'bladeValue',
                        label: 'Blade Value',
                        value: null,
                        unit: 'mm',
                        manual: true,
                        tooltipKey: 'machineConfig.EW05.general.bladeValue'
                    },
                    {
                        key: 'retract',
                        label: 'Retract',
                        value: null,
                        unit: 'mm',
                        manual: false,
                        linkedTo: 'bladeValue',
                        factor: OUTER_RETRACT_FACTOR,
                        ceilOneDecimal: true,
                        tooltipKey: 'machineConfig.EW05.general.retractWithPercent',
                        tooltipParams: { percent: OUTER_RETRACT_PERCENT }
                    },
                    {
                        key: 'rollerLGap',
                        label: 'Roller L. Gap',
                        value: null,
                        unit: 'mm',
                        manual: false,
                        linkedTo: 'bladeValue',
                        factor: 0.7,
                        floorHalf: true,
                        tooltipKey: 'machineConfig.EW05.general.rollerLGap'
                    },
                    {
                        key: 'rollerRGap',
                        label: 'Roller R. Gap',
                        value: null,
                        unit: 'mm',
                        manual: false,
                        linkedTo: 'bladeValue',
                        factor: 0.7,
                        floorHalf: true,
                        tooltipKey: 'machineConfig.EW05.general.rollerRGap'
                    },
                ]
            });

            // === OUTER 1: Guaina esterna (1° passata) ===
            // Se serve 2 passate su un lato, cappiamo a 150mm. Altrimenti target intero.
            sections.push({
                id: 'outer1',
                label: 'OUTER',
                sublabel: hasTwoPass ? 'Sguainatura guaina esterna - 1° passata' : 'Sguainatura guaina esterna',
                sublabelKey: hasTwoPass
                    ? 'machineConfig.EW05.sections.outer1TwoPass'
                    : 'machineConfig.EW05.sections.outer1Single',
                parameters: [
                    {
                        key: 'stripL',
                        label: 'Strip L',
                        value: needsSplitL ? TWO_PASS_THRESHOLD : targetStripL,
                        unit: 'mm',
                        tooltipKey: needsSplitL
                            ? 'machineConfig.EW05.outer1.stripLMax'
                            : 'machineConfig.EW05.outer1.stripL',
                        tooltipParams: needsSplitL ? { max: TWO_PASS_THRESHOLD } : undefined
                    },
                    {
                        key: 'pullOffL',
                        label: 'Pull-off',
                        value: needsSplitL ? (TWO_PASS_THRESHOLD + 5) : (targetStripL + 5),
                        unit: 'mm',
                        manual: false,
                        tooltipKey: 'machineConfig.EW05.outer1.pullOffComplete'
                    },
                    {
                        key: 'stripR',
                        label: 'Strip R',
                        value: needsSplitR ? TWO_PASS_THRESHOLD : targetStripR,
                        unit: 'mm',
                        tooltipKey: needsSplitR
                            ? 'machineConfig.EW05.outer1.stripRMax'
                            : 'machineConfig.EW05.outer1.stripR',
                        tooltipParams: needsSplitR ? { max: TWO_PASS_THRESHOLD } : undefined
                    },
                    {
                        key: 'pullOffR',
                        label: 'Pull-off',
                        value: needsSplitR ? (TWO_PASS_THRESHOLD + 5) : (targetStripR + 5),
                        unit: 'mm',
                        manual: false,
                        tooltipKey: 'machineConfig.EW05.outer1.pullOffRComplete'
                    },
                ]
            });

            // === INNER: Spelatura fili interni (opzionale) ===
            sections.push({
                id: 'inner',
                label: 'INNER',
                sublabel: 'Spelatura cavi interni',
                sublabelKey: 'machineConfig.EW05.sections.inner',
                enabled: innerStripL > 0 || innerStripR > 0,
                parameters: innerStripL > 0 || innerStripR > 0 ? [
                    {
                        key: 'stripL',
                        label: 'Strip L',
                        value: innerStripL,
                        unit: 'mm',
                        tooltipKey: 'machineConfig.EW05.inner.stripL'
                    },
                    {
                        key: 'pullOffL',
                        label: 'Pull-off',
                        value: innerPullCombineL,
                        unit: 'mm',
                        tooltipKey: innerPullCombineL >= innerStripL
                            ? 'machineConfig.EW05.inner.pullOffLComplete'
                            : 'machineConfig.EW05.inner.pullOffLPartial'
                    },
                    {
                        key: 'bladeValue',
                        label: 'Blade Value',
                        value: maxDiametro !== null ? Math.max(1, maxDiametro) : null,
                        unit: 'mm',
                        tooltipKey: 'machineConfig.EW05.inner.bladeValue'
                    },
                    {
                        key: 'retract',
                        label: 'Retract',
                        value: maxDiametro !== null
                            ? Math.ceil(Math.max(1, maxDiametro) * INNER_RETRACT_FACTOR * 10) / 10
                            : null,
                        unit: 'mm',
                        tooltipKey: 'machineConfig.EW05.inner.retractWithPercent',
                        tooltipParams: { percent: INNER_RETRACT_PERCENT }
                    },
                    {
                        key: 'stripR',
                        label: 'Strip R',
                        value: innerStripR,
                        unit: 'mm',
                        tooltipKey: 'machineConfig.EW05.inner.stripR'
                    },
                    {
                        key: 'pullOffR',
                        label: 'Pull-off',
                        value: innerPullCombineR,
                        unit: 'mm',
                        tooltipKey: innerPullCombineR >= innerStripR
                            ? 'machineConfig.EW05.inner.pullOffRComplete'
                            : 'machineConfig.EW05.inner.pullOffRPartial'
                    },
                ] : []
            });

            // === OUTER 2: 2° passata (solo se necessaria) ===
            if (hasTwoPass) {
                // Se c'è slitting, la 2a passata copre fino a SLITTING_THRESHOLD (300mm)
                // altrimenti copre fino al target completo
                const outer2StripL = needsSplitL
                    ? (needsSlittingL ? SLITTING_THRESHOLD : targetStripL)
                    : 0;
                const outer2StripR = needsSplitR
                    ? (needsSlittingR ? SLITTING_THRESHOLD : targetStripR)
                    : 0;

                sections.push({
                    id: 'outer2',
                    label: 'OUTER',
                    sublabel: 'Sguainatura guaina esterna - 2° passata',
                    sublabelKey: 'machineConfig.EW05.sections.outer2',
                    parameters: [
                        {
                            key: 'stripL',
                            label: 'Strip L',
                            value: outer2StripL,
                            unit: 'mm',
                            tooltipKey: outer2StripL > 0
                                ? 'machineConfig.EW05.outer2.stripL'
                                : 'machineConfig.EW05.outer2.stripLNone'
                        },
                        {
                            key: 'pullOffL',
                            label: 'Pull-off',
                            value: outer2StripL === 0 ? 0 : (outer2StripL > 200 ? 200 : outer2StripL + 5),
                            unit: 'mm',
                            manual: false,
                            tooltipKey: outer2StripL === 0
                                ? 'machineConfig.EW05.outer2.pullOffLNone'
                                : outer2StripL > 200
                                    ? 'machineConfig.EW05.outer2.pullOffLMax'
                                    : 'machineConfig.EW05.outer2.pullOffLComplete',
                            tooltipParams: outer2StripL === 0
                                ? undefined
                                : outer2StripL > 200
                                    ? { max: 200 }
                                    : undefined
                        },
                        {
                            key: 'stripR',
                            label: 'Strip R',
                            value: outer2StripR,
                            unit: 'mm',
                            tooltipKey: outer2StripR > 0
                                ? 'machineConfig.EW05.outer2.stripR'
                                : 'machineConfig.EW05.outer2.stripRNone'
                        },
                        {
                            key: 'pullOffR',
                            label: 'Pull-off',
                            value: outer2StripR === 0 ? 0 : (outer2StripR > 200 ? 200 : outer2StripR + 5),
                            unit: 'mm',
                            manual: false,
                            tooltipKey: outer2StripR === 0
                                ? 'machineConfig.EW05.outer2.pullOffRNone'
                                : outer2StripR > 200
                                    ? 'machineConfig.EW05.outer2.pullOffRMax'
                                    : 'machineConfig.EW05.outer2.pullOffRComplete',
                            tooltipParams: outer2StripR === 0
                                ? undefined
                                : outer2StripR > 200
                                    ? { max: 200 }
                                    : undefined
                        },
                    ]
                });
            }

            // === SLITTING: Incisione guaina oltre i 300mm (se necessario) ===
            if (hasSlitting) {
                const cableLength = cable.lunghezza;

                /**
                 * Calcola la lista unificata di slot di incisione, ordinata da sinistra a destra.
                 * - Lato Left (A): posizioni assolute dal bordo sinistro,
                 *   da SLITTING_THRESHOLD+step fino a targetStripL (incluso).
                 * - Lato Right (B): posizioni assolute dal bordo sinistro,
                 *   da cableLength-targetStripR fino a < cableLength-SLITTING_THRESHOLD.
                 */
                const computeUnifiedSlots = (tStripL, tStripR, cLen, segInterval) => {
                    const slots = [];

                    // Left side (A): incisioni verso l'interno partendo dalla zona già spelata
                    if (tStripL > SLITTING_THRESHOLD) {
                        let lastLeftPos = SLITTING_THRESHOLD;
                        for (let pos = SLITTING_THRESHOLD + segInterval; pos <= tStripL; pos += segInterval) {
                            slots.push({ startPosition: pos, peelLength: 0.1 });
                            lastLeftPos = pos;
                        }
                        // Se l'ultimo step non ha raggiunto targetStripL, aggiungi il punto finale
                        if (lastLeftPos < tStripL) {
                            slots.push({ startPosition: tStripL, peelLength: 0.1 });
                        }
                    }

                    // Right side (B): posizioni assolute dal bordo sinistro
                    // partono da (cableLength - targetStripR) e aumentano a step
                    // finché non raggiungono la zona già spelata (cableLength - SLITTING_THRESHOLD)
                    if (tStripR > SLITTING_THRESHOLD) {
                        for (let pos = cLen - tStripR; pos < cLen - SLITTING_THRESHOLD; pos += segInterval) {
                            slots.push({ startPosition: pos, peelLength: 0.1 });
                        }
                    }

                    // Ordina per posizione crescente (da sinistra a destra)
                    slots.sort((a, b) => a.startPosition - b.startPosition);
                    return slots;
                };

                /**
                 * Calcola gli slot in modalità knife (taglio continuo per ogni lato).
                 */
                const computeKnifeSlots = (tStripL, tStripR, cLen) => {
                    const slots = [];
                    if (tStripL > SLITTING_THRESHOLD) {
                        slots.push({ startPosition: SLITTING_THRESHOLD, peelLength: tStripL - SLITTING_THRESHOLD });
                    }
                    if (tStripR > SLITTING_THRESHOLD) {
                        slots.push({ startPosition: cLen - tStripR, peelLength: tStripR - SLITTING_THRESHOLD });
                    }
                    slots.sort((a, b) => a.startPosition - b.startPosition);
                    return slots;
                };

                const effectiveStripL = needsSlittingL ? targetStripL : 0;
                const effectiveStripR = needsSlittingR ? targetStripR : 0;

                sections.push({
                    id: 'slitting',
                    label: 'SLITTING',
                    sublabel: 'Incisione guaina esterna',
                    sublabelKey: 'machineConfig.EW05.sections.slitting',
                    type: 'slitting',
                    slittingData: {
                        defaultSegmentInterval: DEFAULT_SEGMENT_INTERVAL,
                        cableLength,
                        needsSlittingL,
                        needsSlittingR,
                        targetStripL: effectiveStripL,
                        targetStripR: effectiveStripR,
                        remainingL: needsSlittingL ? targetStripL - SLITTING_THRESHOLD : 0,
                        remainingR: needsSlittingR ? targetStripR - SLITTING_THRESHOLD : 0,
                        unifiedSlots: computeUnifiedSlots(effectiveStripL, effectiveStripR, cableLength, DEFAULT_SEGMENT_INTERVAL),
                        knifeSlots: computeKnifeSlots(effectiveStripL, effectiveStripR, cableLength),
                    }
                });
            }

            return sections;
        }
    }
};

export const DEFAULT_MACHINE = 'EW-05';

/**
 * Ottieni la lista delle macchine disponibili per un dropdown/selezione.
 */
export const getMachineList = () => {
    return Object.values(MACHINES).map(m => ({
        id: m.id,
        name: m.name,
        description: m.description
    }));
};
